import { useMemo } from 'react';
import { Highlighter } from 'react-bootstrap-typeahead';

import { searchNodeDefinitions } from 'api/node_definitions/operations/searchNodeDefinitions';

import { EditReference } from './EditReference';
import type { ReferenceSource } from './referenceSource';

// The minimal identifying shape this control deals in. Caller stores
// node_definition_id; the dialog bridges to/from a partial T at the
// callsite (see editTrigger.tsx). `NodeDefinitionSummary` from the
// search endpoint is structurally identical, so it passes straight
// through.
export interface NodeDefinitionReference {
  readonly node_definition_id: number;
  readonly name: string;
  readonly zone_name: string;
}

interface Props {
  readonly value: NodeDefinitionReference | null;
  readonly onChange: (value: NodeDefinitionReference | null) => void;
  readonly label?: string;
  readonly mandatory?: boolean;
  readonly disabled?: boolean;
  // Restrict the searchable set to one zone (server-side `zone_name`
  // equals). Undefined widens to all authorized node definitions.
  readonly zoneName?: string;
}

const getKey = (n: NodeDefinitionReference) => n.node_definition_id;

const getLabel = (n: NodeDefinitionReference): string => n.name;

/**
 * Searchable dropdown for NodeDefinitions, backed by server-side search
 * (`GET /node_definitions?search=…`) so it scales past the client-side
 * row ceiling. Two-column row layout (name + zone, muted right-aligned
 * as informational context); the server matches on name only, zone is
 * shown so users can disambiguate visually.
 */
export const EditNodeDefinitionReference = ({ value, onChange, label = 'Node Definition', mandatory, disabled, zoneName }: Props) => {
  // Re-created when the zone scope changes so the model re-fetches; stable
  // otherwise (a fresh closure would rebuild the model every render).
  const source = useMemo<ReferenceSource<NodeDefinitionReference>>(
    () => ({
      kind: 'search',
      search: (query, limit) =>
        searchNodeDefinitions({
          search: query || undefined,
          limit,
          zone_name: zoneName,
        }),
      // Resolve a saved id back to its label by id alone — independent of
      // the zone scope, so an existing selection still renders.
      resolve: async (key) => {
        const { items } = await searchNodeDefinitions({
          node_definition_id: Number(key),
          limit: 1,
        });
        return items[0] ?? null;
      },
      // Shown while `resolve` is in flight so a key-only value reads as
      // selected rather than blank.
      interimValue: (key) => ({
        node_definition_id: Number(key),
        name: `Node #${key}`,
        zone_name: '',
      }),
    }),
    [zoneName]
  );

  return (
    <EditReference<NodeDefinitionReference>
      label={label}
      value={value}
      onChange={onChange}
      source={source}
      getKey={getKey}
      getLabel={getLabel}
      mandatory={mandatory}
      disabled={disabled}
      renderMenuItemChildren={(n, query) => (
        <div className="d-flex justify-content-between gap-3">
          <span>
            <Highlighter search={query}>{n.name}</Highlighter>
          </span>
          <span className="text-body-secondary">{n.zone_name}</span>
        </div>
      )}
    />
  );
};
